import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import {
  getChainId,
  getCurrentPracticeId,
} from "../../../core/store/chainSlice";
import {
  getIsReleaseSession,
  getPracticeFormId,
} from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { useLogout } from "./handleLogout";
import { useEndReleaseSession } from "./webAppSession";

export const useHandleMissingData = () => {
  const practiceId = useMainStore(getCurrentPracticeId);
  const chainId = useMainStore(getChainId);
  const practiceFormId = useMainStore(getPracticeFormId);
  const logout = useLogout();
  const endReleaseSession = useEndReleaseSession();
  const reported = useRef(false);
  const endingRelease = useRef(false);
  useEffect(() => {
    if (endingRelease.current) {
      return;
    }
    const missing = [
      !practiceId && "practiceId",
      !chainId && "chainId",
      !practiceFormId && "practiceFormId",
    ].filter((key): key is string => !!key);
    if (missing.length === 0) {
      return;
    }
    // The sign-out below drops the patient on the start screen, from where the
    // only move is to sign in again and land back here. That loop leaves no
    // server trace — patientWebAppLogin succeeded — so report it first. Once
    // per mount: `logout` is a new closure each render, so this effect re-runs.
    if (!reported.current) {
      reported.current = true;
      Sentry.captureMessage("Onboarding aborted: missing invite data", {
        level: "error",
        extra: { missing, practiceId, chainId, href: window.location.href },
      });
    }
    // A release session (PF-6133) ends on its own end screen. The marker is read before the logout clears it, and
    // the ref keeps the re-runs of this effect from starting a second logout.
    if (getIsReleaseSession(useMainStore.getState())) {
      endingRelease.current = true;
      void endReleaseSession({ ended: true });
      return;
    }
    logout();
  }, [practiceId, chainId, practiceFormId, logout, endReleaseSession]);
};
